import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../../app/LanguageContext';
import { subtleSurfaceBg } from '../../../theme/surfaceSx';
import MediaGallery from '../../medias/public/MediaGallery';
import { ExtensionSlot } from '../../core/public/ExtensionSlot';
import { CostField } from '../../core/public';
import PriceWithSimulatorField from '../../../extensions/price-simulator-3d/PriceWithSimulatorField';
import { resolveCostForSimulationApply } from '../../../extensions/price-simulator-3d/priceSimulatorMetadata';
import { buildVariantTitle, buildSkuPriceSimulatorContext, isVariantReadOnly } from './productVariantUtils';

const CUSTOMIZABLE_TYPES = ['TEXT', 'NUMBER', 'DATE', 'TEXTAREA', 'SELECT'];

function DialogSection({ title, children, first = false }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{
          display: 'block',
          mt: first ? 0 : 2,
          mb: 1,
          letterSpacing: '0.05em',
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function VariantDetailDialog({
  open,
  variant = null,
  options = [],
  productId = null,
  productName = '',
  productEditPath = '',
  defaultVariableCosts = [],
  onClose,
  onSave,
  onDelete,
  onApplySimulatorPrice,
  saving = false,
}) {
  const { translate } = useTranslation();
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (open && variant) {
      setDraft({
        cost: variant.cost ?? 0,
        price: variant.price ?? 0,
        stock: variant.stock ?? 0,
        status: variant.status ?? 'ACTIVE',
        is_made_to_order: Boolean(variant.is_made_to_order),
        manufacturing_days: variant.manufacturing_days ?? 0,
      });
    } else {
      setDraft(null);
    }
  }, [open, variant]);

  if (!variant || !draft) return null;

  const title = buildVariantTitle(variant, options);
  const readOnly = isVariantReadOnly(variant, options);

  const handleChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    onSave?.(variant.id, draft);
  };

  const handleMadeToOrderChange = (checked) => {
    setDraft((prev) => ({
      ...prev,
      is_made_to_order: checked,
      stock: checked ? 0 : prev.stock,
    }));
  };

  const handleApplySimulatedPrice = async (payload) => {
    await onApplySimulatorPrice?.(variant.id, payload);
    setDraft((prev) => {
      if (!prev) return prev;
      const cost = resolveCostForSimulationApply(payload.simulation, prev.cost ?? variant.cost ?? 0);
      return { ...prev, price: payload.price, cost };
    });
  };

  const simulatorContext = buildSkuPriceSimulatorContext({
    productName,
    variant,
    options,
    defaultVariableCosts,
    readOnly,
    onApplySimulatedPrice: handleApplySimulatedPrice,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {translate('products.variant')}
          </Typography>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
            {options.map((opt) => {
              const val = variant.attributes?.[opt.key] ?? variant.attributes?.[opt.name];
              if (!val) return null;
              return (
                <Chip key={opt.key || opt.name} size="small" label={`${opt.name}: ${val}`} variant="outlined" />
              );
            })}
            {readOnly && (
              <Chip
                size="small"
                label={
                  variant.is_deleted
                    ? translate('products.variant_deleted_label')
                    : translate('products.variant_legacy_label')
                }
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} aria-label={translate('common.close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {readOnly && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('products.variant_legacy_hint')}
          </Typography>
        )}
        <DialogSection title={translate('products.variant_image_section')} first>
          <MediaGallery
            metadataKey="sku_image"
            metadataValue={variant.id}
            accept="image/*"
            compact
            enablePaste={!readOnly}
            readOnly={readOnly}
            metadataLinkUrl={productEditPath}
            selectExistingScope={
              productId ? { metadataKey: 'product_image', metadataValue: productId } : null
            }
          />
        </DialogSection>

        <DialogSection title={translate('products.cost_and_price_section')}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
            }}
          >
            <CostField
              fullWidth
              value={draft.cost ?? 0}
              onChange={(value) => handleChange('cost', value)}
              disabled={readOnly}
            />
            <PriceWithSimulatorField
              label={translate('products.price')}
              value={draft.price ?? 0}
              onChange={(value) => handleChange('price', value)}
              disabled={readOnly}
              readOnly={readOnly}
              showSimulator={!readOnly && Boolean(onApplySimulatorPrice)}
              summaryCompact={false}
              slotId="product.sku.price"
              simulatorContext={simulatorContext}
            />
          </Box>
        </DialogSection>

        <DialogSection title={translate('products.availability_section')}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr)' },
              gap: 1.5,
              alignItems: 'stretch',
              mb: 1,
            }}
          >
            <Box
              sx={{
                bgcolor: subtleSurfaceBg,
                borderRadius: 1,
                p: 1.25,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                minHeight: 100,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="body2" fontWeight={500}>
                  {translate('products.made_to_order')}
                </Typography>
                <Switch
                  size="small"
                  checked={draft.is_made_to_order}
                  onChange={(e) => handleMadeToOrderChange(e.target.checked)}
                  disabled={readOnly}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                {translate('products.made_to_order_no_stock_hint')}
              </Typography>
            </Box>

            <Box sx={{ opacity: draft.is_made_to_order ? 0.45 : 1, pointerEvents: draft.is_made_to_order ? 'none' : 'auto' }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {translate('products.stock_available_label')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={draft.is_made_to_order ? 0 : draft.stock}
                onChange={(e) => handleChange('stock', parseFloat(e.target.value) || 0)}
                disabled={readOnly || draft.is_made_to_order}
                inputProps={{ min: 0 }}
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {translate('products.manufacturing_days')}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={draft.manufacturing_days || ''}
                placeholder={translate('products.manufacturing_days_short_placeholder')}
                onChange={(e) =>
                  handleChange(
                    'manufacturing_days',
                    e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0,
                  )
                }
                inputProps={{ min: 0 }}
                disabled={readOnly}
              />
            </Box>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {translate('products.availability_hint')}
          </Typography>
        </DialogSection>

        <DialogSection title={translate('products.status')}>
          <FormControl fullWidth size="small" disabled={readOnly}>
            <InputLabel>{translate('products.variant_status')}</InputLabel>
            <Select
              label={translate('products.variant_status')}
              value={draft.status}
              onChange={(e) => handleChange('status', e.target.value)}
            >
              <MenuItem value="ACTIVE">{translate('products.variant_status_active')}</MenuItem>
              <MenuItem value="INACTIVE">{translate('products.variant_status_inactive')}</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {translate('products.variant_status_help')}
          </Typography>
        </DialogSection>

        <ExtensionSlot slotId="product-variant-detail" context={{ variant, productId, options }} />

        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {translate('products.sku_code')}: {variant.code || variant.id}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: readOnly ? 'flex-end' : 'space-between', px: 2, py: 1.5, bgcolor: subtleSurfaceBg }}>
        {!readOnly && (
          <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => onDelete?.(variant.id)}>
            {translate('common.delete')}
          </Button>
        )}
        <Box>
          <Button onClick={onClose} sx={{ mr: readOnly ? 0 : 1 }}>
            {readOnly ? translate('common.close') : translate('common.cancel')}
          </Button>
          {!readOnly && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? translate('common.saving') : translate('common.save')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export function CustomizationFieldTypeSelect({ value, onChange, label }) {
  const { translate } = useTranslation();
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value || 'TEXT'} onChange={(e) => onChange(e.target.value)}>
        {CUSTOMIZABLE_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {translate(`products.customization_type_${t.toLowerCase()}`)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
